import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrganizationService } from '../../services/organization.service';
import { Organization } from '../../models/organization.model';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-organizations',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  template: `
    <div class="p-8 max-w-7xl mx-auto">
      <header class="flex justify-between items-end mb-8 border-l-4 border-zaxis-accent pl-4">
        <div>
          <h1 class="text-3xl font-mono font-black tracking-tight text-white uppercase">Instituciones</h1>
          <p class="text-gray-400 mt-2 text-[10px] font-mono tracking-widest uppercase">Gestión global de organizaciones</p>
        </div>
        <button (click)="showCreatePanel = true" class="px-4 py-2 bg-zaxis-accent/10 border border-zaxis-accent text-zaxis-accent font-mono text-[10px] uppercase tracking-widest hover:bg-zaxis-accent hover:text-black transition-colors flex items-center gap-2">
          <i class="ph ph-plus text-lg"></i>
          Nueva Institución
        </button>
      </header>

      <div *ngIf="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <!-- Custom Brutalist Skeleton Loading State -->
        <div *ngFor="let i of [1,2,3,4,5,6]" class="skeleton-box p-5 h-[160px] flex flex-col justify-between">
          <div class="flex justify-between items-start mb-4">
            <div class="skeleton-text w-1/2"></div>
            <div class="skeleton-text w-1/4"></div>
          </div>
          <div class="skeleton-text title w-3/4"></div>
          <div class="space-y-2 mt-4">
            <div class="skeleton-text h-[20px] w-full" style="height: 20px; margin: 0"></div>
            <div class="skeleton-text h-[20px] w-5/6" style="height: 20px; margin: 0"></div>
          </div>
        </div>
      </div>

      <div *ngIf="!loading && organizations.length === 0" class="text-center py-20 bg-black rounded-none border border-zaxis-border">
        <div class="flex justify-center mb-4">
          <i class="ph ph-buildings text-6xl text-gray-600"></i>
        </div>
        <p class="text-gray-400 text-sm font-mono uppercase tracking-widest">No hay instituciones creadas aún.</p>
        <p class="text-gray-500 text-xs font-mono uppercase tracking-widest mt-2">Crea la primera institución para empezar.</p>
      </div>

      <div *ngIf="!loading && organizations.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div *ngFor="let org of organizations"
             [routerLink]="['/organizations', org.id]"
             class="bg-black border border-zaxis-border rounded-none p-5 hover:border-zaxis-accent cursor-pointer transition-all group">

          <!-- Header -->
          <div class="flex items-start justify-between mb-4 border-b border-zaxis-border pb-4">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-none bg-black flex items-center justify-center border border-zaxis-border">
                <i class="ph ph-buildings text-xl text-zaxis-accent"></i>
              </div>
              <div>
                <h3 class="font-mono font-bold text-sm uppercase tracking-widest text-white group-hover:text-zaxis-accent transition-colors">{{ org.name }}</h3>
                <div class="flex items-center gap-1.5 mt-0.5">
                  <p class="text-[9px] text-gray-500 font-mono uppercase tracking-widest">{{ org.id }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Details -->
          <div class="space-y-2 text-[10px] font-mono uppercase tracking-widest">
            <div class="flex justify-between text-gray-500">
              <span>Owner UID</span>
              <span class="text-gray-300 truncate w-32 text-right" [title]="org.ownerUid">{{ org.ownerUid }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Slide-over Panel -->
      <div *ngIf="showCreatePanel" class="fixed inset-0 z-50 flex justify-end">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" (click)="showCreatePanel = false"></div>
        <div class="relative w-full max-w-md bg-zaxis-card border-l border-zaxis-border shadow-none flex flex-col animate-panel-in">
          <div class="flex items-center justify-between p-6 border-b border-zaxis-border">
            <h2 class="text-[11px] font-mono uppercase tracking-widest text-gray-400">Nueva Institución</h2>
            <button (click)="showCreatePanel = false" class="text-gray-500 hover:text-white transition-colors text-xl font-mono">&times;</button>
          </div>

          <div class="flex-1 p-6 space-y-6">
            <div>
              <label class="block text-[11px] text-gray-500 font-mono uppercase tracking-widest mb-2">Nombre de la Institución</label>
              <input
                [(ngModel)]="newOrgName"
                type="text"
                class="w-full bg-zaxis-dark border border-zaxis-border px-4 py-3 text-white focus:outline-none focus:border-zaxis-accent transition-all font-mono tracking-widest uppercase rounded-none"
                placeholder="EJ: KAIROS TEST INC">
            </div>

            <div *ngIf="createError" class="text-zaxis-danger text-[11px] font-mono tracking-wide bg-zaxis-danger/10 p-3 border border-zaxis-danger/20">
              {{ createError }}
            </div>

            <button
              (click)="createOrganization()"
              [disabled]="isCreating || !newOrgName.trim()"
              class="px-6 py-3 bg-zaxis-accent hover:bg-cyan-400 text-black font-mono font-bold uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full rounded-none">
              {{ isCreating ? 'CREANDO...' : 'CREAR INSTITUCIÓN' }}
            </button>
          </div>
        </div>
      </div>

    </div>
  `,
  styles: [`
    @keyframes slideIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
    }
    .animate-panel-in {
      animation: slideIn 0.3s ease-out forwards;
    }
  `]
})
export class OrganizationsComponent implements OnInit {
  private orgService = inject(OrganizationService);

  organizations: Organization[] = [];
  loading = true;
  
  showCreatePanel = false;
  isCreating = false;
  newOrgName = '';
  createError = '';

  ngOnInit() {
    this.loadOrganizations();
  }

  loadOrganizations() {
    this.loading = true;
    this.orgService.getOrganizations().subscribe({
      next: (res) => {
        this.organizations = res || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading organizations', err);
        this.loading = false;
      }
    });
  }

  createOrganization() {
    if (!this.newOrgName.trim()) return;

    this.isCreating = true;
    this.createError = '';

    this.orgService.createOrganization(this.newOrgName).subscribe({
      next: (org) => {
        if (org) {
          this.organizations.unshift(org);
        }
        this.isCreating = false;
        this.showCreatePanel = false;
        this.newOrgName = '';
      },
      error: (err) => {
        this.createError = 'No se pudo crear la institución.';
        this.isCreating = false;
      }
    });
  }
}
